import type { JSX } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { currentColors } from "../../shared/colors";
import NoteIcon from "../../shared/components/icons/NoteIcon";
import TaskIcon from "../../shared/components/icons/TaskIcon";
import type { ShareAnswersType, SubmittedTaskStatus, TaskIcon as TaskIconType } from "../../shared/types";
import { taskFinished, taskSharingAnswers } from "./query";

interface IProps {
    taskChoice: {
        id: string;
        icon: TaskIconType;
        name: string;
        url: string;
        complete: boolean;
    };
    tasks: {
        id: string;
        icon: TaskIconType;
        shortTitle: string;
        url: string;
        shareAnswers: ShareAnswersType;
        submittedTask: {
            id: string;
            url: string;
            status: SubmittedTaskStatus;
        } | null;
    }[];
    current: string;
}

const Nav = styled.nav`
    position: relative;
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: 80px;
    font-size: 0.85rem;
    justify-content: start;
    gap: 20px;
    overflow-x: auto;
    margin-bottom: 0.5rem;
    @media (min-width: 640px) {
        margin-bottom: 1rem;
    }
`;

interface IStyledTaskIcon {
    $current: boolean;
}

const StyledTaskIcon = styled(TaskIcon)<IStyledTaskIcon>`
    margin-top: 10px;
    width: 60px;
    height: 60px;
    background-color: white;
    border-bottom: 6px solid ${({ $current }) => ($current ? currentColors.border : "transparent")};
    padding-bottom: 4px;
    margin-bottom: 2px;
`;

const StyledLink = styled(Link)`
    position: relative;
    display: grid;
    justify-items: center;
    text-align: center;
    color: ${currentColors.text};
    &:hover, &:focus, &:active {
        color: ${currentColors.text};
    }
`;

const AllAnswersIcon = styled(NoteIcon)`
    width: 22px;
    height: 22px;
    position: absolute;
    top: 0px;
    left: 3px;
`;

function taskUrl(task: IProps["tasks"][0]): string {
    if (task?.submittedTask?.status === "accepted") {
        return task.submittedTask?.url;
    }
    return task.url;
}

function TaskChoiceNav({ taskChoice, tasks, current }: IProps): JSX.Element {
    return (
        <Nav>
            <StyledLink to={taskChoice.url}>
                <StyledTaskIcon icon={taskChoice.icon} $current={false} active={taskChoice.complete} />
                <div>{taskChoice.name}</div>
            </StyledLink>
            {tasks.map((task) => (
                <StyledLink to={taskUrl(task)} key={task.id}>
                    <StyledTaskIcon icon={task.icon} active={taskFinished(task)} $current={current === task.id} />
                    <div>{task.shortTitle}</div>
                    {taskSharingAnswers(task) && <AllAnswersIcon />}
                </StyledLink>
            ))}
        </Nav>
    );
}

export default TaskChoiceNav;
